import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["monthTitle", "daysContainer", "loading", "popover", "popoverContent", "calendarContainer"];

  connect() {
    this.currentDate = new Date();
    this.sessions = [];
    this.loadSessions();
  }

  async loadSessions() {
    this.showLoading(true);
    try {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth() + 1;

      const response = await fetch(`/wp-json/mezcalito/v1/sessions?year=${year}&month=${month}`);
      this.sessions = await response.json();

      this.renderCalendar();
    } catch (error) {
      // Error silencieuse en production
    } finally {
      this.showLoading(false);
    }
  }

  renderCalendar() {
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    const monthName = monthNames[this.currentDate.getMonth()];
    const year = this.currentDate.getFullYear();
    this.monthTitleTarget.textContent = `${monthName} ${year}`;

    const firstDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
    const lastDay = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);

    // Ajuster pour que lundi soit le premier jour (0 = lundi)
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    this.daysContainerTarget.innerHTML = '';

    // Ajouter les jours vides au début
    for (let i = 0; i < firstDayOfWeek; i++) {
      const emptyDay = document.createElement('div');
      emptyDay.className = 'c-agenda__day c-agenda__day--empty';
      this.daysContainerTarget.appendChild(emptyDay);
    }

    // Ajouter tous les jours du mois
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dayElement = this.createDayElement(day);
      this.daysContainerTarget.appendChild(dayElement);
    }
  }

  createDayElement(day) {
    const dayElement = document.createElement('div');
    dayElement.className = 'c-agenda__day';

    const dateStr = this.formatDate(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, day);
    const daySessions = this.getSessionsForDate(dateStr);
    const multiDayIndicators = this.getMultiDayIndicatorsForDate(dateStr);

    const dayNumber = document.createElement('div');
    dayNumber.className = 'c-agenda__day-number';
    dayNumber.textContent = day;
    dayElement.appendChild(dayNumber);

    const dayInner = document.createElement('div');
    dayInner.className = 'c-agenda__day-inner';
    dayElement.appendChild(dayInner);

    // Marquer le jour actuel
    const today = new Date();
    if (
      day === today.getDate() &&
      this.currentDate.getMonth() === today.getMonth() &&
      this.currentDate.getFullYear() === today.getFullYear()
    ) {
      dayElement.classList.add('c-agenda__day--today');
    }

    // Ajouter les indicateurs de sessions multi-jours
    if (multiDayIndicators.length > 0) {
      const indicatorsContainer = document.createElement('div');
      indicatorsContainer.className = 'c-agenda__multiday-indicators';

      multiDayIndicators.forEach(indicator => {
        const indicatorElement = this.createMultiDayIndicator(indicator);
        indicatorsContainer.appendChild(indicatorElement);
      });

      dayInner.appendChild(indicatorsContainer);
    }

    // Ajouter les sessions d'un seul jour
    if (daySessions.length > 0) {
      dayElement.classList.add('c-agenda__day--has-sessions');

      const sessionsContainer = document.createElement('div');
      sessionsContainer.className = 'c-agenda__sessions';

      daySessions.forEach(session => {
        const sessionElement = this.createSessionElement(session);
        sessionsContainer.appendChild(sessionElement);
      });

      dayInner.appendChild(sessionsContainer);
    }

    return dayElement;
  }

  getMultiDayIndicatorsForDate(dateStr) {
    const indicators = [];
    const currentDate = new Date(dateStr);

    this.sessions.forEach(session => {
      if (!session.end_date || session.end_date === session.date) return;

      const startDate = new Date(session.date);
      const endDate = new Date(session.end_date);

      if (currentDate >= startDate && currentDate <= endDate) {
        const duration = this.calculateDuration(session.date, session.end_date);
        if (duration > 1) {
          let position = 'middle';
          if (currentDate.getTime() === startDate.getTime()) {
            position = 'start';
          } else if (currentDate.getTime() === endDate.getTime()) {
            position = 'end';
          }

          indicators.push({
            session: session,
            position: position,
            duration: duration
          });
        }
      }
    });

    return indicators;
  }

  createMultiDayIndicator(indicator) {
    const indicatorElement = document.createElement('button');
    indicatorElement.className = `c-agenda__multiday-indicator c-agenda__multiday-indicator--${indicator.position}`;
    indicatorElement.type = 'button';
    indicatorElement.dataset.action = 'click->calendar#openSession';
    indicatorElement.dataset.sessionId = indicator.session.variation_id;
    indicatorElement.dataset.productId = indicator.session.product_id;
    indicatorElement.dataset.fullTitle = indicator.session.title;

    // Tooltip natif du navigateur
    indicatorElement.title = indicator.session.title;

    const sessionId = `session-multiday-${indicator.session.variation_id}`;
    indicatorElement.id = sessionId;
    indicatorElement.dataset.sessionElementId = sessionId;

    if (indicator.session.category_color) {
      indicatorElement.style.setProperty('--session-color', indicator.session.category_color);
    }

    const borderColor = this.generateBorderColor(indicator.session.variation_id);
    indicatorElement.style.setProperty('--border-color', borderColor);

    indicatorElement.innerHTML = `
      <span class="c-agenda__multiday-title">${indicator.session.title}</span>
      <span class="c-agenda__multiday-duration">${indicator.duration}j</span>
    `;

    return indicatorElement;
  }

  generateBorderColor(variationId) {
    const colors = [
      '#4caf50', '#2196f3', '#ff9800', '#e91e63', '#9c27b0',
      '#00bcd4', '#ffeb3b', '#f44336', '#795548', '#607d8b'
    ];

    const index = variationId % colors.length;
    return colors[index];
  }

  createSessionElement(session) {
    const sessionElement = document.createElement('button');
    sessionElement.className = 'c-agenda__session';
    sessionElement.type = 'button';
    sessionElement.dataset.action = 'click->calendar#openSession';
    sessionElement.dataset.sessionId = session.variation_id;
    sessionElement.dataset.productId = session.product_id;
    sessionElement.dataset.fullTitle = session.title;

    // Tooltip natif du navigateur
    sessionElement.title = session.title;

    const sessionId = `session-${session.variation_id}`;
    sessionElement.id = sessionId;
    sessionElement.dataset.sessionElementId = sessionId;

    if (session.category_color) {
      sessionElement.style.setProperty('--session-color', session.category_color);
    }
    
    sessionElement.innerHTML = `
      <span class="c-agenda__session-title">${session.title}</span>
    `;

    return sessionElement;
  }

  calculateDuration(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  }

  getSessionsForDate(dateStr) {
    return this.sessions.filter(session => {
      if (session.date !== dateStr) return false;

      if (!session.end_date || session.end_date === session.date) {
        return true;
      }

      const duration = this.calculateDuration(session.date, session.end_date);
      return duration === 1;
    });
  }

  formatDate(year, month, day) {
    const m = month < 10 ? `0${month}` : month;
    const d = day < 10 ? `0${day}` : day;
    return `${year}-${m}-${d}`;
  }

  truncate(str, maxLength) {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '...';
  }

  async openSession(event) {
    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const variationId = button.dataset.sessionId;

    this.popoverContentTarget.innerHTML = `
      <div class="c-agenda-popover__loader">
        <div class="c-agenda__spinner"></div>
        <p>Chargement de la session...</p>
      </div>
    `;

    this.popoverTarget.showPopover();
    this.positionPopover(button);

    try {
      const response = await fetch(`/wp-json/mezcalito/v1/session-detail?product_id=${productId}&variation_id=${variationId}`);
      const data = await response.json();

      this.popoverContentTarget.innerHTML = data.html;
      this.positionPopover(button);
    } catch (error) {
      // Error silencieuse en production
      this.popoverContentTarget.innerHTML = `
        <div class="c-agenda-popover__error">
          <p>Erreur lors du chargement de la session.</p>
        </div>
      `;
    }
  }

  positionPopover(anchorElement) {
    const popover = this.popoverTarget;
    const rect = anchorElement.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();

    let top = rect.bottom + 8;
    let left = rect.left;

    if (top + popoverRect.height > window.innerHeight) {
      top = rect.top - popoverRect.height - 8;
    }

    if (left + popoverRect.width > window.innerWidth) {
      left = window.innerWidth - popoverRect.width - 16;
    }

    if (left < 0) {
      left = 16;
    }

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  previousMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() - 1);
    this.loadSessions();
  }

  nextMonth() {
    this.currentDate.setMonth(this.currentDate.getMonth() + 1);
    this.loadSessions();
  }

  showLoading(show) {
    if (this.hasLoadingTarget && this.hasCalendarContainerTarget) {
      if (show) {
        this.loadingTarget.classList.add('c-agenda__loading--active');
        this.calendarContainerTarget.classList.add('c-agenda__calendar--loading');
      } else {
        this.loadingTarget.classList.remove('c-agenda__loading--active');
        this.calendarContainerTarget.classList.remove('c-agenda__calendar--loading');
      }
    }
  }
}
